import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { api } from '../../services/api';

interface PlayerPreview {
  playerExternalId: string;
  playerName: string;
  position: string;
  teamAbbreviation: string;
  mappedPlayerId: string | null;
  mappedPlayerName: string | null;
  isInPool: boolean;
  rawStats: Record<string, number>;
  fantasyPoints: number;
  breakdown: Record<string, { value: number; points: number }>;
}

interface GamePreview {
  externalGameId: string;
  matchedEventId: string | null;
  homeTeam: { abbreviation: string; score: number };
  awayTeam: { abbreviation: string; score: number };
  isFinal: boolean;
  isChampionshipGame: boolean;
  players: PlayerPreview[];
  warnings: string[];
}

interface StatsPreview {
  date: string;
  poolId: string;
  sport: string;
  games: GamePreview[];
  summary: {
    totalGames: number;
    finalGames: number;
    totalPlayers: number;
    mappedPlayers: number;
    unmappedPlayers: number;
    playersInPool: number;
  };
  warnings: string[];
}

interface ApplyResult {
  statsCreated: number;
  statsUpdated: number;
  eventsUpdated: number;
  scoresRecalculated: number;
  errors: string[];
}

interface StatsDate {
  date: string;
  gamesTotal: number;
  gamesWithStats: number;
  /** Number of events with status=COMPLETED. Backend may not return this for older deploys. */
  gamesCompleted?: number;
  /** Number of events that are both COMPLETED and have a FANTASY_POINTS stat. */
  gamesFullyApplied?: number;
  /** True only when every event on this date is both COMPLETED and has stats. */
  statsImported: boolean;
  /** Three-state visual status. Backend may omit on older deploys; we derive from other fields then. */
  status?: 'FULL' | 'PARTIAL' | 'NONE';
}

export function AdminStats() {
  const { poolId } = useParams<{ poolId: string }>();
  const { getAccessTokenSilently } = useAuth0();

  // State
  const [date, setDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [preview, setPreview] = useState<StatsPreview | null>(null);
  const [applyResult, setApplyResult] = useState<ApplyResult | null>(null);
  const [statsDates, setStatsDates] = useState<StatsDate[]>([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedGames, setExpandedGames] = useState<Set<string>>(new Set());
  const [selectedGames, setSelectedGames] = useState<Set<string>>(new Set());
  const [poolName, setPoolName] = useState('');
  const [poolType, setPoolType] = useState('');

  useEffect(() => {
    if (poolId) {
      loadPoolInfo();
      loadStatsDates();
    }
  }, [poolId]);

  async function loadPoolInfo() {
    try {
      const pool = await api.getPool(poolId!);
      setPoolName(pool.name);
      setPoolType(pool.type);
    } catch {
      // Non-critical
    }
  }

  async function loadStatsDates() {
    try {
      const token = await getAccessTokenSilently();
      const data = await api.getStatsDates(poolId!, token);
      setStatsDates(Array.isArray(data) ? data : data.dates || []);
    } catch {
      // Non-critical
    }
  }

  async function fetchPreview() {
    if (!poolId || !date) return;
    setLoading(true);
    setError(null);
    setPreview(null);
    setApplyResult(null);

    try {
      const token = await getAccessTokenSilently();
      const data = await api.fetchStatsPreview(poolId, date, token);
      setPreview(data);
      // Select all final games by default
      const finalGameIds = new Set<string>(
        data.games.filter((g: GamePreview) => g.isFinal).map((g: GamePreview) => g.externalGameId)
      );
      setSelectedGames(finalGameIds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch stats');
    } finally {
      setLoading(false);
    }
  }

  async function applySelectedStats() {
    if (!poolId || !date || selectedGames.size === 0) return;
    setApplying(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const result = await api.applyStats(
        poolId,
        date,
        Array.from(selectedGames),
        token,
      );
      setApplyResult(result);
      loadStatsDates();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply stats');
    } finally {
      setApplying(false);
    }
  }

  function toggleGame(gameId: string) {
    setSelectedGames(prev => {
      const next = new Set(prev);
      if (next.has(gameId)) next.delete(gameId);
      else next.add(gameId);
      return next;
    });
  }

  function toggleExpanded(gameId: string) {
    setExpandedGames(prev => {
      const next = new Set(prev);
      if (next.has(gameId)) next.delete(gameId);
      else next.add(gameId);
      return next;
    });
  }

  const sport = poolType?.includes('NHL')
    ? 'NHL'
    : poolType?.includes('NBA')
      ? 'NBA'
      : poolType?.includes('NFL')
        ? 'NFL'
        : '';
  const sourceLabel = sport === 'NHL' ? 'NHL API' : sport === 'NBA' ? 'NBA.com API' : 'ESPN NFL API';

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/admin" className="text-blue-600 hover:text-blue-800 text-sm mb-2 inline-block">
            &larr; Back to Dashboard
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            {sport} Stats Import {poolName && `— ${poolName}`}
          </h1>
          <p className="text-gray-500 mt-1">
            Fetch game stats from {sourceLabel}, review, then apply to calculate scores.
          </p>
        </div>
      </div>

      {/* Date Selector + Fetch Button */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Game Date</label>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={fetchPreview}
            disabled={loading || !date}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium"
          >
            {loading ? 'Fetching...' : 'Fetch Stats'}
          </button>
        </div>

        {/* Quick date buttons — game dates with their fully-applied status.
            Sorted descending (newest first) and capped at 14 so the most recent
            activity is always visible.
            Three chip states:
              ✓ green   — fully applied (every game COMPLETED + has stats)
              ⏳ amber  — partially applied (some games done, others pending)
              ○ gray    — events exist but nothing applied yet for this date */}
        {(() => {
          // Derive status if backend hasn't been redeployed yet (graceful fallback).
          const withStatus = statsDates.map((sd) => {
            const status: 'FULL' | 'PARTIAL' | 'NONE' =
              sd.status ??
              (sd.statsImported
                ? 'FULL'
                : sd.gamesWithStats > 0
                ? 'PARTIAL'
                : 'NONE');
            return { ...sd, _status: status };
          });
          const recent = withStatus
            .slice()
            .sort((a, b) => b.date.localeCompare(a.date))
            .slice(0, 14);
          if (recent.length === 0) return null;
          return (
            <div className="mt-4">
              <p className="text-xs text-gray-500 mb-2 flex items-center gap-3 flex-wrap">
                <span>Game dates <span className="text-gray-400">(most recent first)</span>:</span>
                <span className="inline-flex items-center gap-1 text-green-700">
                  <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>
                  fully applied
                </span>
                <span className="inline-flex items-center gap-1 text-amber-700">
                  <span className="inline-block w-2 h-2 rounded-full bg-amber-500"></span>
                  partial
                </span>
                <span className="inline-flex items-center gap-1 text-gray-600">
                  <span className="inline-block w-2 h-2 rounded-full bg-gray-400"></span>
                  pending
                </span>
              </p>
              <div className="flex flex-wrap gap-2">
                {recent.map((sd) => {
                  const isSelected = sd.date === date;
                  const tone = isSelected
                    ? 'bg-blue-600 text-white border-blue-600'
                    : sd._status === 'FULL'
                    ? 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100'
                    : sd._status === 'PARTIAL'
                    ? 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100'
                    : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100';
                  const fullyApplied = sd.gamesFullyApplied ?? (sd.statsImported ? sd.gamesTotal : sd.gamesWithStats);
                  const dateLabel = new Date(sd.date + 'T12:00:00').toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  });
                  const indicator =
                    sd._status === 'FULL' ? '✓' : sd._status === 'PARTIAL' ? '⏳' : '○';
                  const title =
                    sd._status === 'FULL'
                      ? `Fully applied (${sd.gamesTotal}/${sd.gamesTotal} games)`
                      : sd._status === 'PARTIAL'
                      ? `Partial: ${fullyApplied}/${sd.gamesTotal} games applied`
                      : `Pending — ${sd.gamesTotal} game${sd.gamesTotal !== 1 ? 's' : ''}, none applied yet`;
                  return (
                    <button
                      key={sd.date}
                      onClick={() => setDate(sd.date)}
                      title={title}
                      className={`px-3 py-1 text-xs rounded-full border transition-colors ${tone}`}
                    >
                      {dateLabel} {indicator}
                      {sd._status === 'PARTIAL' && (
                        <span className="ml-1 opacity-75">
                          {fullyApplied}/{sd.gamesTotal}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
          {error}
        </div>
      )}

      {/* Apply Result */}
      {applyResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-green-800 mb-2">Stats Applied Successfully</h3>
          <div className="text-sm text-green-700 space-y-1">
            <p>Player stats created: {applyResult.statsCreated}</p>
            <p>Player stats updated: {applyResult.statsUpdated}</p>
            <p>Events marked complete: {applyResult.eventsUpdated}</p>
            <p>Entry scores recalculated: {applyResult.scoresRecalculated}</p>
            {applyResult.errors.length > 0 && (
              <div className="mt-2 text-red-600">
                <p className="font-medium">Errors:</p>
                {applyResult.errors.map((e, i) => <p key={i}>• {e}</p>)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats Preview */}
      {preview && (
        <div>
          {/* Summary */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold mb-3">
              {preview.sport} Games on {new Date(preview.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-center">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-2xl font-bold">{preview.summary.totalGames}</div>
                <div className="text-xs text-gray-500">Total Games</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-green-600">{preview.summary.finalGames}</div>
                <div className="text-xs text-gray-500">Final</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-2xl font-bold">{preview.summary.totalPlayers}</div>
                <div className="text-xs text-gray-500">Players</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-blue-600">{preview.summary.mappedPlayers}</div>
                <div className="text-xs text-gray-500">Mapped</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-orange-600">{preview.summary.unmappedPlayers}</div>
                <div className="text-xs text-gray-500">Unmapped</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-purple-600">{preview.summary.playersInPool}</div>
                <div className="text-xs text-gray-500">In Pool</div>
              </div>
            </div>

            {/* Warnings */}
            {preview.warnings.length > 0 && (
              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="font-medium text-yellow-800 text-sm mb-1">Warnings:</p>
                {preview.warnings.map((w, i) => (
                  <p key={i} className="text-sm text-yellow-700">• {w}</p>
                ))}
              </div>
            )}
          </div>

          {/* Games List */}
          <div className="space-y-4 mb-6">
            {preview.games.map(game => {
              const isExpanded = expandedGames.has(game.externalGameId);
              const isSelected = selectedGames.has(game.externalGameId);
              const poolPlayers = game.players.filter(p => p.isInPool);

              return (
                <div
                  key={game.externalGameId}
                  className={`bg-white rounded-lg shadow overflow-hidden ${
                    isSelected ? 'ring-2 ring-blue-500' : ''
                  }`}
                >
                  {/* Game Header */}
                  <div className="flex items-center p-4 gap-4">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleGame(game.externalGameId)}
                      className="w-5 h-5 rounded text-blue-600"
                      disabled={!game.isFinal}
                    />
                    <div className="flex-1 cursor-pointer" onClick={() => toggleExpanded(game.externalGameId)}>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold">
                          {game.awayTeam.abbreviation} {game.awayTeam.score} @ {game.homeTeam.abbreviation} {game.homeTeam.score}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          game.isFinal
                            ? 'bg-green-100 text-green-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {game.isFinal ? 'Final' : 'In Progress'}
                        </span>
                        {game.isChampionshipGame && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">
                            Championship (2x)
                          </span>
                        )}
                        {!game.matchedEventId && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                            No Matching Event
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {game.players.length} players &middot; {poolPlayers.length} in pool
                        {poolPlayers.length > 0 && (
                          <span className="ml-2 text-blue-600">
                            (top: {poolPlayers.sort((a, b) => b.fantasyPoints - a.fantasyPoints)[0]?.playerName} — {poolPlayers.sort((a, b) => b.fantasyPoints - a.fantasyPoints)[0]?.fantasyPoints} pts)
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => toggleExpanded(game.externalGameId)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      {isExpanded ? '▲' : '▼'}
                    </button>
                  </div>

                  {/* Game Warnings */}
                  {game.warnings.length > 0 && (
                    <div className="px-4 pb-2">
                      {game.warnings.map((w, i) => (
                        <p key={i} className="text-xs text-yellow-600">⚠ {w}</p>
                      ))}
                    </div>
                  )}

                  {/* Expanded Player Stats */}
                  {isExpanded && (
                    <div className="border-t px-4 py-3">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-gray-500 border-b">
                            <th className="pb-2 font-medium">Player</th>
                            <th className="pb-2 font-medium">Pos</th>
                            <th className="pb-2 font-medium">Team</th>
                            <th className="pb-2 font-medium">Status</th>
                            <th className="pb-2 font-medium text-right">Fantasy Pts</th>
                            <th className="pb-2 font-medium">Breakdown</th>
                          </tr>
                        </thead>
                        <tbody>
                          {game.players
                            .sort((a, b) => b.fantasyPoints - a.fantasyPoints)
                            .map(player => (
                              <tr
                                key={player.playerExternalId}
                                className={`border-b last:border-0 ${
                                  player.isInPool
                                    ? 'bg-blue-50'
                                    : player.mappedPlayerId
                                    ? ''
                                    : 'bg-gray-50 text-gray-400'
                                }`}
                              >
                                <td className="py-2">
                                  <div>{player.playerName}</div>
                                  {player.mappedPlayerName && player.mappedPlayerName !== player.playerName && (
                                    <div className="text-xs text-gray-400">→ {player.mappedPlayerName}</div>
                                  )}
                                </td>
                                <td className="py-2">{player.position}</td>
                                <td className="py-2">{player.teamAbbreviation}</td>
                                <td className="py-2">
                                  {player.isInPool ? (
                                    <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">In Pool</span>
                                  ) : player.mappedPlayerId ? (
                                    <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">Mapped</span>
                                  ) : (
                                    <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-600">Unmapped</span>
                                  )}
                                </td>
                                <td className="py-2 text-right font-semibold">{player.fantasyPoints}</td>
                                <td className="py-2">
                                  <div className="flex flex-wrap gap-1">
                                    {Object.entries(player.breakdown).map(([cat, { value, points }]) => (
                                      <span key={cat} className="text-xs px-1.5 py-0.5 rounded bg-gray-100">
                                        {cat}: {value} ({points > 0 ? '+' : ''}{points})
                                      </span>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Apply Button */}
          {preview.games.some(g => g.isFinal) && (
            <div className="bg-white rounded-lg shadow p-6 sticky bottom-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">
                    {selectedGames.size} game{selectedGames.size !== 1 ? 's' : ''} selected for import
                  </p>
                  <p className="text-sm text-gray-500">
                    This will create player stats, mark events as completed, and recalculate entry scores.
                  </p>
                </div>
                <button
                  onClick={applySelectedStats}
                  disabled={applying || selectedGames.size === 0}
                  className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors font-semibold text-lg"
                >
                  {applying ? 'Applying...' : 'Apply Stats & Score'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!loading && !preview && !error && (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          <p className="text-lg mb-2">Select a game date and click "Fetch Stats" to get started.</p>
          <p className="text-sm">Stats will be fetched from the {sourceLabel} for all finished games on that date.</p>
          <p className="text-sm mt-1 text-gray-400">Game events are created automatically — no need to import a schedule.</p>
        </div>
      )}
    </div>
  );
}
